import { useState, useEffect, useRef } from 'react';
import {
  CurrencyInput,
  IconButton,
  Stack,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  CheckIcon,
  CloseIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';

const safeStr = (v) => String(v == null ? 0 : v);
const safeNum = (v) => (v == null || isNaN(Number(v)) ? 0 : Number(v));

/**
 * Inline editable cell for Approved Amount fields in variance buckets.
 * Manages its own local state so it doesn't lose focus on parent re-renders.
 * Syncs back to `initialValue` after a successful save triggers a data refetch.
 */
const ApprovedAmountCell = ({ initialValue, onSave }) => {
  const [localValue, setLocalValue] = useState(safeStr(initialValue));
  const [isSaving, setIsSaving] = useState(false);
  const prevRef = useRef(initialValue);

  // Sync local state when initialValue changes (e.g. after save + refetch)
  useEffect(() => {
    if (prevRef.current !== initialValue) {
      setLocalValue(safeStr(initialValue));
      prevRef.current = initialValue;
    }
  }, [initialValue]);

  const isDirty = safeNum(localValue) !== safeNum(initialValue);

  const handleSave = async () => {
    if (!isDirty || isSaving) return;
    setIsSaving(true);
    try {
      await onSave(safeNum(localValue));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => setLocalValue(safeStr(initialValue));

  return (
    <CurrencyInput
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      disabled={isSaving}
      helperText={null}
      endAdornment={
        <Stack direction={'row'} sx={{ marginRight: 0.5 }}>
          <IconButton
            aria-label="save approved amount"
            size="small"
            disabled={!isDirty || isSaving}
            onClick={handleSave}
            sx={{ color: isDirty ? 'success.main' : 'text.disabled' }}
          >
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label="cancel approved amount edit"
            size="small"
            disabled={!isDirty || isSaving}
            onClick={handleCancel}
            sx={{ color: isDirty ? 'error.main' : 'text.disabled' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      }
    />
  );
};

export default ApprovedAmountCell;
